import type { GeoPoint, Position } from '@/signalr';

export { accuracyColor } from '@/ui';

function formatN0(value: number, language: string): string {
  return new Intl.NumberFormat(language || 'en', { maximumFractionDigits: 0 }).format(value);
}

export function formatHeight(height: number | null | undefined): string | undefined {
  return height != null ? `${height} cm` : undefined;
}

export function formatWeight(weight: number | null | undefined): string | undefined {
  return weight != null ? `${weight} kg` : undefined;
}

export function formatEedHeight(height: number | null | undefined): string | undefined {
  return height != null ? `${Math.floor(height * 100)} cm` : undefined;
}

export function formatEedWeight(weight: number | null | undefined): string | undefined {
  return weight != null ? `${Math.floor(weight)} kg` : undefined;
}

export function formatAsListOfStrings(strings: readonly string[] | null | undefined): string | undefined {
  return strings != null ? strings.join(', ') : undefined;
}

export function formatAccuracy(accuracy: number, language: string): string {
  return `${formatN0(accuracy, language)} m`;
}

export function formatAltitude(position: Position, language: string): string {
  let text = `${formatN0(position.altitude, language)} m`;
  if (position.verticalAccuracy != null) {
    text += ` ± ${formatN0(position.verticalAccuracy, language)} m`;
  }

  return text;
}

function formatDecimalInvariant(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  }).format(value);
}

export function formatCoordinates(geoPoint: GeoPoint | null | undefined): string | undefined {
  if (geoPoint == null) {
    return undefined;
  }

  const latitudeChar = geoPoint.latitude < 0 ? 'S' : 'N';
  const longitudeChar = geoPoint.longitude < 0 ? 'W' : 'E';
  return `${formatDecimalInvariant(geoPoint.latitude)}${latitudeChar} ${formatDecimalInvariant(geoPoint.longitude)}${longitudeChar}`;
}

function angleToDegree(degrees: number, directionChar: string): string {
  const minutes = (degrees % 1) * 60;
  const seconds = (minutes % 1) * 60;
  return `${Math.floor(degrees)}° ${Math.floor(minutes)}' ${Math.floor(seconds)}"${directionChar}`;
}

export function formatCoordinatesToDegree(geoPoint: GeoPoint | null | undefined): string | undefined {
  if (geoPoint == null) {
    return undefined;
  }

  const latitude = angleToDegree(geoPoint.latitude, geoPoint.latitude < 0 ? 'S' : 'N');
  const longitude = angleToDegree(geoPoint.longitude, geoPoint.longitude < 0 ? 'W' : 'E');
  return `${latitude} ${longitude}`;
}
